// Service for managing saved companies
import { Company } from '../types/CompanyTypes';
import { savedCompaniesAPI } from './api/savedCompanies.api';

export interface SavedCompany extends Company {
  savedAt: Date;
  notes?: string;
  status?: 'active' | 'following' | 'archived';
}

class SavedCompaniesService {
  private baseUrl = '/api/saved-companies';
  private mockSavedCompanies: Map<string, SavedCompany[]> = new Map(); // Mock storage for development

  // Save a company
  async saveCompany(userId: string, company: Company, notes?: string): Promise<SavedCompany> {
    try {
      // Use backend API if available and not default localhost
      if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
        return await savedCompaniesAPI.saveCompany(userId, company, notes);
      }
      
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        const savedCompany: SavedCompany = {
          ...company,
          savedAt: new Date(),
          notes,
          status: 'following'
        };
        
        const userCompanies = this.mockSavedCompanies.get(userId) || [];
        userCompanies.push(savedCompany);
        this.mockSavedCompanies.set(userId, userCompanies);
        
        // Store in localStorage for persistence
        localStorage.setItem(`savedCompanies_${userId}`, JSON.stringify(userCompanies));
        
        return savedCompany;
      }
      
      const response = await fetch(`${this.baseUrl}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId,
          company,
          notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save company');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error saving company:', error);
      throw error;
    }
  }

  // Get user's saved companies
  async getSavedCompanies(userId: string, filters?: {
    status?: string;
    industry?: string;
    location?: string;
  }): Promise<SavedCompany[]> {
    try {
      // Use backend API if available and not default localhost
      if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
        return await savedCompaniesAPI.getSavedCompanies(userId);
      }
      
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        // Load from localStorage
        const stored = localStorage.getItem(`savedCompanies_${userId}`);
        const companies = stored ? JSON.parse(stored) : [];
        this.mockSavedCompanies.set(userId, companies);
        
        // Apply filters if provided
        let filteredCompanies = companies;
        if (filters) {
          if (filters.status) {
            filteredCompanies = filteredCompanies.filter((company: SavedCompany) => company.status === filters.status);
          }
          if (filters.industry) {
            filteredCompanies = filteredCompanies.filter((company: SavedCompany) => company.industry === filters.industry);
          }
          if (filters.location) {
            filteredCompanies = filteredCompanies.filter((company: SavedCompany) => 
              company.locations?.some(loc => loc.city.includes(filters.location!) || loc.country.includes(filters.location!))
            );
          }
        }
        
        return filteredCompanies;
      }
      
      const queryParams = new URLSearchParams({ userId });
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const response = await fetch(`${this.baseUrl}?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch saved companies');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching saved companies:', error);
      throw error;
    }
  }

  // Remove a saved company
  async removeSavedCompany(userId: string, companyId: string): Promise<void> {
    try {
      // Use backend API if available and not default localhost
      if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
        return await savedCompaniesAPI.removeSavedCompany(userId, companyId);
      }
      
      // For development, use mock implementation
      if (process.env.NODE_ENV === 'development' || !this.baseUrl.startsWith('http')) {
        const userCompanies = this.mockSavedCompanies.get(userId) || [];
        const filteredCompanies = userCompanies.filter(company => company.id !== companyId);
        this.mockSavedCompanies.set(userId, filteredCompanies);
        
        // Update localStorage
        localStorage.setItem(`savedCompanies_${userId}`, JSON.stringify(filteredCompanies));
        
        return;
      }
      
      const response = await fetch(`${this.baseUrl}/${companyId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        throw new Error('Failed to remove saved company');
      }
    } catch (error) {
      // console.error('Error removing saved company:', error);
      throw error;
    }
  }

  // Update saved company (e.g., add notes, change status)
  async updateSavedCompany(
    userId: string, 
    companyId: string, 
    updates: Partial<SavedCompany>
  ): Promise<SavedCompany> {
    try {
      const response = await fetch(`${this.baseUrl}/${companyId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({
          userId,
          updates
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update saved company');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error updating saved company:', error);
      throw error;
    }
  }

  // Get saved companies statistics
  async getSavedCompaniesStats(userId: string): Promise<{
    totalSaved: number;
    following: number;
    activeJobPostings: number;
    avgRating: number;
    uniqueIndustries: number;
    lastSavedAt: Date;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/stats?userId=${userId}`, {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch saved companies statistics');
      }

      return await response.json();
    } catch (error) {
      // console.error('Error fetching saved companies statistics:', error);
      throw error;
    }
  }

  // Helper method to get auth token
  private getAuthToken(): string {
    // This should be implemented based on your auth system
    return localStorage.getItem('authToken') || '';
  }
}

export const savedCompaniesService = new SavedCompaniesService();